import React, { useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Container,
  useTheme,
  Link,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import CustomInput from "../../shared/CustomInput";
import InfoSection from "../../shared/InfoSection";
import img from "../../../assets/images/comment-dots.png";
import phone from "../../../assets/images/Calling.png";
import mail from "../../../assets/images/Mail.png";
import location from "../../../assets/images/Location.png";
import facebook from "../../../assets/images/face.png";
import instgram from "../../../assets/images/inst.png";
import linkedin from "../../../assets/images/link.png";
import twitter from "../../../assets/images/tw.png";
import CustomButton from "../../shared/CustomButton";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../shared/Loader";
import { fetchContactData } from "../../../redux/slices/ContactSlice";

const ContactForm = ({ showInfoSection = true }) => {
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;
  const dispatch = useDispatch();

  const { data, status, error } = useSelector((state) => state.Contact);
  useEffect(() => {
    dispatch(fetchContactData());
  }, [dispatch]);

  if (status === "loading") {
    return <Loader />;
  }

  if (status === "failed") {
    return <div>Error: {error}</div>;
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t("contact.Name is required")),
    email: Yup.string()
      .email(t("contact.Invalid email"))
      .required(t("contact.Email is required")),
    mobile: Yup.string().required(t("contact.Mobile number is required")),
    message: Yup.string().required(t("contact.Message is required")),
  });

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/contact`,
        {
          name: values.name,
          phone: values.mobile,
          email: values.email,
          message: values.message,
        }
      );


      if (response.status === 201) {
        toast.success(t("missed.successMessage"));
        resetForm();
      } else {
        console.error("Unexpected server response:", response);
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting the form:", error);
      toast.error(t("missed.errorMessage"));
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{ mt: { xs: "40px", md: "88px" }, mb: "88px", overflow: "hidden" }}
    >
      {showInfoSection && (
        <Box sx={{ mb: "40px" }}>
          <InfoSection
            icon={img}
            title={t("buttons.Contact us")}
            description={t("missed.Get in Touch")}
            textAlign="center"
          />
        </Box>
      )}

      <Grid
        container
        spacing={4}
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              width: "auto",
              height: "auto",
              padding: "48px 32px",
              borderRadius: "12px",
              border: "1px solid #D6D8DB",
            }}
            elevation={0}
          >
            <Typography
              sx={{
                fontSize: "22px",
                fontWeight: "700",
                color: theme.palette.primary.title,
                mb: 3,
              }}
            >
              {t("contact.Contact info")}
            </Typography>
            <Link
              href={`tel:${data?.data?.settings?.phone1}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  gap: "5px",
                }}
              >
                <img
                  src={phone}
                  alt="Phone Icon"
                  width="24"
                  height="24"
                  style={{
                    width: "24px",
                    height: "24px",
                    marginRight: "8px",
                    objectFit: "contain",
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: theme.palette.primary.title,
                    }}
                  >
                    {t("contact.Mobile number")}{" "}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "18px", color: theme.palette.primary.body }}
                  >
                    {data?.data?.settings?.phone1}
                  </Typography>
                </Box>
              </Box>
            </Link>
            <Link
              href={`mailto:${data?.data?.settings?.email}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  gap: "5px",
                }}
              >
                <img
                  src={mail}
                  alt="mail Icon"
                  width="24"
                  height="24"
                  style={{
                    width: "24px",
                    height: "24px",
                    marginRight: "8px",
                    objectFit: "contain",
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: theme.palette.primary.title,
                    }}
                  >
                    {t("contact.Email")}{" "}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "18px", color: theme.palette.primary.body }}
                  >
                    {data?.data?.settings?.email}
                  </Typography>
                </Box>
              </Box>
            </Link>

            <Link
              href={`https://www.google.com/maps?q=:${data?.data?.settings?.location[currentLang]}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  gap: "5px",
                }}
              >
                <img
                  src={location}
                  alt="location Icon"
                  width="24"
                  height="24"
                  style={{
                    width: "24px",
                    height: "24px",
                    marginRight: "8px",
                    objectFit: "contain",
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: theme.palette.primary.title,
                    }}
                  >
                    {t("contact.Location")}{" "}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "18px", color: theme.palette.primary.body }}
                  >
                    {data?.data?.settings?.location[currentLang]}
                  </Typography>
                </Box>
              </Box>
            </Link>
            <Typography
              sx={{
                fontSize: "22px",
                fontWeight: "700",
                color: theme.palette.primary.title,
              }}
              mt={5}
              mb={3}
            >
              {t("contact.Social media")}{" "}
            </Typography>
            <Box sx={{ display: "flex", gap: 3 }}>
              <Box
                component="a"
                href={data?.data?.settings?.facebook || "#"}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: "44px",
                  height: "44px",
                  border: "1px solid #D6D8DB",
                  borderRadius: "12px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                <Box
                  component="img"
                  src={facebook}
                  alt="facebook"
                  sx={{
                    width: "auto",
                    height: "19px",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box
                component="a"
                href={data?.data?.settings?.instagram}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: "44px",
                  height: "44px",
                  border: "1px solid #D6D8DB",
                  borderRadius: "12px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                <Box
                  component="img"
                  src={instgram}
                  alt="instgram"
                  sx={{
                    width: "auto",
                    height: "19px",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box
                component="a"
                href={data?.data?.settings?.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: "44px",
                  height: "44px",
                  border: "1px solid #D6D8DB",
                  borderRadius: "12px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                <Box
                  component="img"
                  src={linkedin}
                  alt="linkedin"
                  sx={{
                    width: "auto",
                    height: "19px",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box
                component="a"
                href={data?.data?.settings?.x}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: "44px",
                  height: "44px",
                  border: "1px solid #D6D8DB",
                  borderRadius: "12px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                <Box
                  component="img"
                  src={twitter}
                  alt="Twitter"
                  sx={{
                    width: "auto",
                    height: "19px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Formik
            initialValues={{ name: "", email: "", mobile: "", message: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ getFieldProps, errors, touched }) => (
              <Form>
                <CustomInput
                  label={t("contact.Name")}
                  placeholder={t("contact.Name")}
                  {...getFieldProps("name")}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  sx={{ mb: 3 }}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: currentLang === "ar" ? "right" : "left",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginRight: 0,
                      marginLeft: 0,
                    },
                  }}
                />
                <CustomInput
                  label={t("contact.Email")}
                  placeholder={t("contact.Email")}
                  {...getFieldProps("email")}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  sx={{ mb: 3 }}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: currentLang === "ar" ? "right" : "left",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginRight: 0,
                      marginLeft: 0,
                    },
                  }}
                />
                <CustomInput
                  label={t("contact.Mobile number")}
                  placeholder={t("contact.Mobile number")}
                  {...getFieldProps("mobile")}
                  error={touched.mobile && Boolean(errors.mobile)}
                  helperText={touched.mobile && errors.mobile}
                  sx={{ mb: 3 }}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: currentLang === "ar" ? "right" : "left",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginRight: 0,
                      marginLeft: 0,
                    },
                  }}
                />
                <CustomInput
                  label={t("contact.Message")}
                  placeholder={t("contact.Message")}
                  isTextarea
                  rows={5}
                  error={touched.message && Boolean(errors.message)}
                  helperText={touched.message && errors.message}
                  FormHelperTextProps={{
                    textAlign: currentLang === "ar" ? "right" : "left",
                  }}
                  {...getFieldProps("message")}
                  sx={{ mb: 5 }}
                />

                <CustomButton
                  type="submit"
                  backgroundImage="linear-gradient(90deg, #0A1F52 0%, #1646B8 100%)"
                >
                  {t("buttons.Send message")}
                </CustomButton>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContactForm;
